// components/dashboard/user-landing-page/courseModalTable.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { IconButton, TextField, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import actionIcon from "../../../assets/images/course/action-icon.svg";
import { toast } from "react-toastify";
import QuizReviewDisplay from 'components/courses/viewcourse/components/QuizReviewDisplay';
import SubjectiveQuizResultViewer from 'components/dashboard/user-landing-page/SubjectiveQuizResultViewer';
import { fetchParticipationPerformance } from "ServerAPI/serverAPI";

export const ContentModalTable = ({
    show,
    handleClose,
    contentData,
    handleViewContent,
    onCodeView
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingRows, setLoadingRows] = useState({});
    const [quizData, setQuizData] = useState(null);
    const [selectedContentId, setSelectedContentId] = useState(null);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [showSubjectiveQuizModal, setShowSubjectiveQuizModal] = useState(false);
    const [subjectiveQuizData, setSubjectiveQuizData] = useState(null);
    const [contentTitles, setContentTitles] = useState({});
    const [reviewConfirmState, setReviewConfirmState] = useState({ show: false, content: null, quizData: null });

    // Extract real data
    const contentList = contentData?.list || [];
    const courseId = contentData?.courseId;
    const userId = contentData?.userId;

    // Fetch actual contentTitle for quizSubjective items when modal opens
    useEffect(() => {
        if (!show || !courseId || !userId || contentList.length === 0) return;

        const fetchTitles = async () => {
            const subjectiveItems = contentList.filter(item => {
                const type = item.contentType?.toLowerCase();
                return type === 'quizsubjective' && (!item.contentTitle || item.contentTitle === 'Untitled' || item.contentTitle.toLowerCase() === 'quizsubjective');
            });

            if (subjectiveItems.length === 0) return;

            const promises = subjectiveItems.map(async (item) => {
                try {
                    const payload = { courseId, contentId: item.contentId, userId, type: "quiz" };
                    const response = await fetchParticipationPerformance(payload);
                    if (response?.status === 200 && response.data?.quiz?.contentTitle) {
                        return { contentId: item.contentId, title: response.data.quiz.contentTitle };
                    }
                } catch (err) {
                    console.error(`Error fetching title for ${item.contentId}:`, err);
                }
                return null;
            });

            const results = await Promise.all(promises);
            const titles = {};
            results.forEach(r => { if (r) titles[r.contentId] = r.title; });
            if (Object.keys(titles).length > 0) {
                setContentTitles(titles);
            }
        };

        fetchTitles();
    }, [show, courseId, userId, contentList]);

    const filteredData = useMemo(() => {
        const initialData = contentList.filter(item => {
            const type = item.contentType?.toLowerCase();
            return ['quiz', 'codesandbox', 'subjective', 'quizsubjective'].includes(type);
        });

        if (!searchTerm) return initialData;

        return initialData.filter(item =>
            (item.contentTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.contentType || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [contentList, searchTerm]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    // Max-Attempts-specific: only quizzes that explicitly opted into the feature carry
    // `isMaxAttemptsQuiz`; fall back to the presence of maxAttemptsUsed/maxAttemptsHistory
    // for records saved before that flag existed.
    const usesAttemptTracking = (content) =>
        content.isMaxAttemptsQuiz !== undefined
            ? Boolean(content.isMaxAttemptsQuiz)
            : (content.maxAttemptsUsed !== undefined && content.maxAttemptsUsed !== null) &&
              Array.isArray(content.maxAttemptsHistory) && content.maxAttemptsHistory.length > 0;
    const getMaxAttemptsUsed = (content) => {
        if (content.maxAttemptsUsed !== undefined && content.maxAttemptsUsed !== null) return content.maxAttemptsUsed;
        if (Array.isArray(content.maxAttemptsHistory)) return content.maxAttemptsHistory.length;
        return 0;
    };

    // "View" is normally gated on `completed`, which hides failed-but-attempted students from
    // review entirely. The relaxation to also allow viewing on any attempt (pass or fail) only
    // applies to quizzes that actually opted into Max Attempts — plain old-flow quizzes
    // (isMaxAttemptsQuiz false/undefined) keep the original, stricter gate exactly as before
    // this feature existed. Without this scoping, the general attemptCount field (which the
    // backend still increments regardless of the flag) would incorrectly unblock viewing for
    // every quiz. Max-Attempts quizzes don't populate that general field at all (only
    // maxAttemptsUsed/maxAttemptsHistory), so a failed-but-attempted one is checked via
    // getMaxAttemptsUsed instead.
    const isViewable = (content) =>
        usesAttemptTracking(content)
            ? (content.completed || getMaxAttemptsUsed(content) > 0 || Boolean(content.quizResults))
            : content.completed;

    const columns = useMemo(() => [
        {
            id: 'sno',
            header: 'S.No.',
            cell: ({ row }) => row.index + 1,
            enableSorting: false
        },
        {
            accessorKey: 'contentTitle',
            header: 'Content Name',
            cell: ({ row, getValue }) => {
                const content = row.original;
                const rawTitle = getValue();
                
                // For quizSubjective, use fetched title if available, otherwise use rawTitle
                const title = contentTitles[content.contentId] || 
                             rawTitle || 
                             content.quiz?.contentTitle || 
                             content.quizData?.contentTitle ||
                             content.localContent?.contentTitle ||
                             'N/A';
                
                return (
                    <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                        {title}
                    </div>
                );
            }
        },
        {
            accessorKey: 'contentType',
            header: 'Type',
            cell: ({ row }) => {
                const { contentType, finalAssessment } = row.original;
                const value = (contentType || '').toLowerCase();
                let displayValue = '--';
                if (value === 'quiz') displayValue = finalAssessment ? 'Final Assessment' : 'Quiz';
                else if (value === 'quizsubjective') displayValue = 'Subjective Quiz';
                else if (value === 'codesandbox') displayValue = 'Coding Exercise';
                else if (value === 'subjective') displayValue = 'Subjective Question';
                return <span className="text-truncate" style={{ maxWidth: '150px' }}>{displayValue}</span>;
            }
        },
        {
            accessorKey: 'maxAttemptsUsed',
            header: () => <span style={{ fontWeight: 'bold', color: '#224D99' }}>Attempts</span>,
            cell: ({ row }) => {
                const content = row.original;
                // Exact match only — Max Attempts was never built for quizSubjective, and
                // `.includes('quiz')` was wrongly matching it too (it contains "quiz").
                const isQuiz = content.contentType?.toLowerCase() === 'quiz';
                if (!isQuiz || !usesAttemptTracking(content)) return null;
                return <span className="badge bg-light text-dark border">{getMaxAttemptsUsed(content)}</span>;
            }
        },
        {
            accessorKey: 'completed',
            header: 'Status',
            cell: ({ row }) => {
                const content = row.original;
                const completed = content.completed;
                const isQuiz = content.contentType?.toLowerCase()?.includes('quiz');
                const hasResults = Boolean(content.quizResults || content.score !== undefined);
                
                // If it wasn't completed but has results + NO start time AND all zero marks, it's a missed scheduled quiz (Absent)
                const resultsList = content.quizResults?.results || [];
                
                // Check if the student actually interacted with the quiz (provided any answer)
                const hasInteracted = resultsList.length > 0 && resultsList.some(r => 
                    r.userAnswer !== undefined && r.userAnswer !== null && r.userAnswer !== ""
                );

                // For old quizzes where final_score is missing, we don't treat it as zero for absence check
                const allZeroMarks = resultsList.length > 0 
                    ? resultsList.every(r => Object.prototype.hasOwnProperty.call(r, 'final_score') ? (r.final_score || 0) === 0 : false) 
                    : true;

                const isAbsent = !completed && isQuiz && hasResults && !content.quizResults?.quizStartedAt && allZeroMarks && !hasInteracted;

                let badgeClass = 'bg-secondary';
                let statusText = 'Pending';
                
                if (completed) {
                    badgeClass = 'bg-success';
                    statusText = 'Completed';
                } else if (isAbsent) {
                    badgeClass = 'bg-danger';
                    statusText = 'Absent';
                } else if (content.teacherReviewed && content.quizResults?.passed === false) {
                    badgeClass = 'bg-warning text-dark';
                    statusText = 'Review: Redo';
                } else if (usesAttemptTracking(content) && (getMaxAttemptsUsed(content) > 0 || Boolean(content.quizResults))) {
                    // Only surface "Attempted" (implying it's reviewable) for quizzes where the
                    // View button actually unblocks on it — old-flow quizzes stay "Pending" until
                    // `completed`, so the badge never promises access the click won't honor.
                    badgeClass = 'bg-warning text-dark';
                    statusText = 'Attempted';
                }

                return (
                    <span className={`badge ${badgeClass}`}>
                        {statusText}
                    </span>
                );
            }
        },
        /* {
            accessorKey: 'teacherReviewed',
            header: 'Teacher Assessed',
            cell: ({ row }) => {
                const content = row.original;
                const contentType = (content.contentType || '').toLowerCase();
                
                // Only show Yes/No for quizSubjective, show N/A for others
                if (contentType !== 'quizsubjective') {
                    return (
                        <span className="badge bg-secondary">
                            N/A
                        </span>
                    );
                }
                
                const teacherReviewed = content.teacherReviewed;
                return (
                    <span className={`badge ${teacherReviewed ? 'bg-success' : 'bg-warning'}`}>
                        {teacherReviewed ? 'Yes' : 'No'}
                    </span>
                );
            }
        }, */
        {
            accessorKey: 'view',
            header: () => <span style={{ fontWeight: 'bold', color: '#224D99' }}>View</span>,
            cell: ({ row }) => {
                const content = row.original;
                const rowKey = content.contentId;
                const isPending = !isViewable(content);

                const handleView = async () => {
                    if (isPending) {
                        toast.info("Content must be attempted to view the review.");
                        return;
                    }
                    setLoadingRows(prev => ({ ...prev, [rowKey]: true }));
                    try {
                        const type = content.contentType?.toLowerCase();

                        if (type === 'quizsubjective') {
                            console.log("=== SUBJECTIVE QUIZ DATA ===");
                            console.log("Local Content Data:", content);

                            try {
                                const payload = {
                                    courseId,
                                    contentId: content.contentId,
                                    userId,
                                    type: "quiz"
                                };
                                const response = await fetchParticipationPerformance(payload);
                                console.log("Fetched Performance Data (API Response):", response?.data);

                                if (response?.status === 200 && response.data) {
                                    // Extract contentTitle from quiz data if available
                                    const quizContentTitle = response.data?.quiz?.contentTitle || 
                                                           response.data?.contentTitle ||
                                                           content.contentTitle;
                                    
                                    // Merge API data with local content data, including updated title
                                    const mergedData = {
                                        ...response.data,
                                        localContent: {
                                            ...content,
                                            contentTitle: quizContentTitle || content.contentTitle
                                        }
                                    };
                                    console.log("Merged Data being passed to SubjectiveQuizResultViewer:", mergedData);
                                    setSubjectiveQuizData(mergedData);
                                    setSelectedContentId(content.contentId);
                                    setShowSubjectiveQuizModal(true);
                                } else {
                                    console.warn("Fetch failed or empty, using local data only");
                                    setSubjectiveQuizData({ localContent: content });
                                    setSelectedContentId(content.contentId);
                                    setShowSubjectiveQuizModal(true);
                                }
                            } catch (err) {
                                console.error("Error fetching subjective quiz details:", err);
                                setSubjectiveQuizData({ localContent: content });
                                setSelectedContentId(content.contentId);
                                setShowSubjectiveQuizModal(true);
                            }
                            return;
                        }

                        if (type === 'quiz') {
                            const payload = { courseId, contentId: content.contentId, userId, type: "quiz" };
                            const res = await fetchParticipationPerformance(payload);
                            if (res?.status === 200) {
                                const isReviewed = content.teacherReviewed === true;
                                if (isReviewed) {
                                    setReviewConfirmState({
                                        show: true,
                                        content: content,
                                        quizData: res.data
                                    });
                                } else {
                                    setQuizData(res.data);
                                    setShowQuizModal(true);
                                    setSelectedContentId(content.contentId);
                                }
                            }
                        } else if (type === 'codesandbox') {
                            await onCodeView(content);
                        } else if (type === 'subjective') {
                            toast.info("Single subjective questions are not supported in this view.");
                        } else {
                            await handleViewContent(content.contentId);
                        }
                    } catch (err) {
                        toast.error("Error loading content");
                    } finally {
                        setLoadingRows(prev => ({ ...prev, [rowKey]: false }));
                    }
                };

                return (
                    <Tooltip title={isPending ? "Pending" : "View"}>
                        <span>
                            <IconButton onClick={handleView} disabled={loadingRows[rowKey]}>
                                {loadingRows[rowKey] ? <CircularProgress size={24} /> : <img src={actionIcon} alt="view" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            }
        }
    ], [onCodeView, handleViewContent, loadingRows, courseId, userId, contentTitles]);

    return (
        <>
            <Modal show={show} onHide={handleClose} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Contents</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between mb-3">
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search by name or type"
                            InputProps={{ startAdornment: <SearchIcon /> }}
                            onChange={handleSearch}
                            value={searchTerm}
                            style={{ maxWidth: '400px' }}
                        />
                    </div>

                    {filteredData.length === 0 ? (
                        <div className="text-center p-5 text-muted">No contents available</div>
                    ) : (
                        <TanstackTable data={filteredData} columns={columns} pagination={true} />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Confirmation Modal for Already Reviewed Quizzes */}
            <Modal
                show={reviewConfirmState.show}
                onHide={() => setReviewConfirmState({ show: false, content: null, quizData: null })}
                centered
                style={{ zIndex: 1060 }}
                contentClassName="border border-2 border-primary shadow-lg rounded-3"
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Review Already Completed</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">Review for this student is already done. Do you still want to make any changes?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setReviewConfirmState({ show: false, content: null, quizData: null })}
                    >
                        No
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            setQuizData(reviewConfirmState.quizData);
                            setSelectedContentId(reviewConfirmState.content.contentId);
                            setShowQuizModal(true);
                            setReviewConfirmState({ show: false, content: null, quizData: null });
                        }}
                    >
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>

            <QuizReviewDisplay
                show={showQuizModal}
                handleClose={() => { setShowQuizModal(false); setQuizData(null); setSelectedContentId(null); }}
                quizData={quizData}
                courseId={courseId}
                contentId={selectedContentId}
                userId={userId}
            />

            <Modal
                show={showSubjectiveQuizModal}
                onHide={() => { setShowSubjectiveQuizModal(false); setSubjectiveQuizData(null); setSelectedContentId(null); }}
                fullscreen={true}
            >
                <Modal.Header className="bg-light border-bottom d-flex justify-content-between align-items-center">
                    <Modal.Title className="fw-bold text-primary fs-3 mb-0">
                        Subjective Quiz Review
                    </Modal.Title>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => { setShowSubjectiveQuizModal(false); setSubjectiveQuizData(null); setSelectedContentId(null); }}
                        style={{ fontSize: '1.5rem', opacity: 0.7 }}
                    ></button>
                </Modal.Header>
                <Modal.Body className="p-4 bg-light">
                    {subjectiveQuizData && (
                        <SubjectiveQuizResultViewer
                            quizData={subjectiveQuizData}
                            courseId={courseId}
                            userId={userId}
                            contentId={selectedContentId}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};